





























import Vue from 'vue';
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import {
	GetNewTitlesDocument,
	GetNewTitlesQuery,
	GetNewTitlesQueryVariables,
} from '@/pages/graphql/queries/GetNewTitles.query';

import { DailySpecials } from '@/interfaces/specials';
import { FormattedNewTitle, NewTitle } from '@/interfaces/new-title-graphql';
import { NewOffer } from '@/interfaces/offer-graphql';
import { NewDateRangeBucket, NewPageType, ObjectType, PageInfo, Platform, TitleFilter } from '@/@types/graphql-types';
import { UserSettings } from '@/stores/user.store';

import { FilterCollection } from '@/stores/filter.store';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import ToastHelper from '@/helpers/toast/toast-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';
import { onTitleQuickActionEventHandler } from '@/helpers/title-action-helper';
import { TitleListName } from '@/interfaces/responses/title-list';

import {
	getSponsoredRecommendations,
	showSponsoredRecommendations,
	SponsoredRecommendationData,
	sponsoredRecommendationProviders,
} from '@/components/sponsored-recommendations/sponsored-recommendation-config';

const Language = namespace('language');
const Filter = namespace('filter');
const Specials = namespace('specials');
const SponsoredRecommendations = namespace('sponsoredRecommendations');
const User = namespace('user');

interface ProviderTitlesData {
	edges: {
		newOffer: NewOffer;
		node: NewTitle;
	}[];
	pageInfo: PageInfo;
	totalCount: number;
}

@Component({
	name: 'ProviderTimeline',
	components: {
		HorizontalTitleListGraphql,
		ProviderIcon,
	},
	apollo: {
		providerTitlesData: {
			query: GetNewTitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetNewTitlesQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				this.isFirstPageLoaded = true;
				return data.newTitles;
			},
			error() {
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class ProviderTimeline extends Vue {
	private isFirstPageLoaded = false;
	providerTitlesData: ProviderTitlesData = {} as ProviderTitlesData;

	@Prop({ required: true }) timelineDate: string;
	@Prop({ required: true }) providerId: number;
	@Prop({ default: null }) titleCount: number;
	@Prop({ default: '' }) providerShortName: string;
	@Prop({ default: null }) dayIndex: number;
	@Prop({ default: null }) providerIndex: number;
	@Prop({ default: null }) bucketType: NewDateRangeBucket;
	@Prop({ default: '' }) sponsoredRecommendationPosition: string;

	@Filter.Getter('new') filtersNew: FilterCollection;

	@Language.Getter language: string;
	@Language.Getter country: string;

	@Specials.Getter specials: Record<string, DailySpecials>;

	@SponsoredRecommendations.Getter isSponsoredRecommendationCohort: boolean;

	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.Getter isPremium: boolean;
	@User.State settings: UserSettings;

	@Emit('onPosterClick')
	onPosterClick($event: { index: number; title: FormattedNewTitle }) {
		return { ...$event, providerId: this.providerId, date: this.timelineDate };
	}

	@Watch('filtersNew')
	changedFilters() {
		this.isFirstPageLoaded = false;
	}

	get newPageType() {
		return this.$route?.meta?.newPageType as NewPageType;
	}

	get IMG_NEW_TIMELINE_ROWS() {
		return global.IMG_NEW_TIMELINE_ROWS;
	}

	get IMG_POSTER_LOAD_COUNT() {
		return global.IMG_POSTER_LOAD_COUNT;
	}

	get isPriceDropsTimeline(): boolean {
		return this.newPageType === NewPageType.PriceDrop;
	}

	get formattedProviderTitles() {
		const providerTitleEdges = this.providerTitlesData?.edges || [];
		return providerTitleEdges.map(edge => ({
			...edge.node,
			newOffer: edge.newOffer,
		}));
	}

	get graphqlFilters(): TitleFilter {
		const selectedProviders =
			this.filtersNew.providers?.length === 0 ? this.settings.providers : this.filtersNew.providers;
		return toGraphqlFilters(this.filtersNew, selectedProviders, this.isHideSeenDislikedTitlesEnabled);
	}

	get queryVariables(): GetNewTitlesQueryVariables {
		const addBucketTypeToPayload =
			[NewPageType.LeavingSoon, NewPageType.Upcoming].includes(this.newPageType) && this.bucketType;

		return {
			date: this.timelineDate,
			filter: {
				...this.graphqlFilters,
				objectTypes: this.filterObjectTypes,
				packages: [this.providerShortName],
			},
			language: this.language,
			country: this.country,
			priceDrops: this.isPriceDropsTimeline,
			platform: Platform.Web,
			pageType: this.newPageType,
			showDateBadge: [NewDateRangeBucket.Next_7Days, NewDateRangeBucket.Next_30Days].includes(this.bucketType),
			availableToPackages: [this.providerShortName],
			...(addBucketTypeToPayload ? { bucketType: this.bucketType } : {}),
		};
	}

	get isLoading() {
		return this.$apollo.queries.providerTitlesData.loading;
	}

	// We need this mapper due to filter naming on the New Page
	// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
	get filterObjectTypes() {
		return this.graphqlFilters?.objectTypes?.map(objectType =>
			objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
		);
	}

	get displaySponsoredRecommendation() {
		const sponsoredRecommendationProviderIDs = getSponsoredRecommendations(this.country).map(
			(sr: SponsoredRecommendationData) => {
				return sr.provider_id;
			}
		);
		return (
			showSponsoredRecommendations(this.country) &&
			this.isSponsoredRecommendationCohort &&
			sponsoredRecommendationProviderIDs.includes(this.providerId) &&
			!this.isPremium &&
			this.timelineDate === this.sponsoredRecommendationPosition
		);
	}

	get titleCountLabel() {
		return this.titleCount === 1
			? this.$t('WEBAPP_TITLE', { NUMBER: this.titleCount })
			: this.$t('WEBAPP_TITLES', { NUMBER: this.titleCount });
	}

	imagesLoadCount(dayIndex: number, providerIndex: number) {
		return !dayIndex && providerIndex < this.IMG_NEW_TIMELINE_ROWS ? this.IMG_POSTER_LOAD_COUNT : 0;
	}

	mounted() {
		onTitleQuickActionEventHandler(({ type, titleObjectId, isTitleInList }) => {
			switch (type) {
				case TitleListName.DISLIKELIST:
					this.isHideSeenDislikedTitlesEnabled && !isTitleInList
						? this.removeTitleFromGraphqlCache(titleObjectId)
						: this.updateListEntryInGraphqlCache(titleObjectId, 'dislikelistEntry', isTitleInList);
					break;
				case TitleListName.SEENLIST:
					if (this.isHideSeenDislikedTitlesEnabled && !isTitleInList) {
						this.removeTitleFromGraphqlCache(titleObjectId);
					}
					break;
				default:
					break;
			}
		});
	}

	newTitlesFromGraphqlCache() {
		return this.$apollo.getClient().readQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
			query: GetNewTitlesDocument,
			variables: this.queryVariables,
		});
	}

	removeTitleFromGraphqlCache(titleObjectId: number) {
		const titles = this.newTitlesFromGraphqlCache();

		let newTitles = [];

		if (titles?.newTitles?.edges?.length) {
			newTitles = titles.newTitles.edges;

			newTitles = newTitles.filter(item => {
				return item.node.__typename === 'Season' && item.node.show
					? item.node.show.objectId !== titleObjectId
					: item.node.objectId !== titleObjectId;
			});

			this.$apollo.getClient().writeQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
				query: GetNewTitlesDocument,
				variables: this.queryVariables,
				data: {
					newTitles: {
						...titles.newTitles,
						totalCount: titles?.newTitles.totalCount - 1,
						edges: newTitles,
					},
				},
			});
		}
	}

	updateListEntryInGraphqlCache(
		titleObjectId: number,
		listName: 'dislikelistEntry' | 'likelistEntry' | 'watchlistEntry' | 'seenlistEntry',
		isTitleInList: boolean
	) {
		const titles = this.newTitlesFromGraphqlCache();
		if (titles?.newTitles?.edges) {
			const updatedTitles = titles.newTitles.edges.map(title => {
				if (title.node.objectId === titleObjectId) {
					const shouldClearOppositeListEntry =
						!isTitleInList && (listName === 'dislikelistEntry' || listName === 'likelistEntry');
					const oppositeListEntryName =
						listName === 'dislikelistEntry' ? 'likelistEntry' : 'dislikelistEntry';

					return {
						...title,
						node: {
							...title.node,
							...(shouldClearOppositeListEntry ? { [oppositeListEntryName]: null } : {}),
							[listName]: isTitleInList
								? null
								: { createdAt: new Date().toISOString(), __typename: 'TitleListEntry' },
						},
					};
				}

				return title;
			});

			this.$apollo.getClient().writeQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
				query: GetNewTitlesDocument,
				variables: this.queryVariables,
				data: {
					newTitles: {
						...titles.newTitles,
						totalCount: titles?.newTitles.totalCount,
						edges: updatedTitles,
					},
				},
			});
		}
	}

	onLoadMore(complete: Function) {
		complete &&
			this.$apollo.queries.providerTitlesData
				.fetchMore({
					variables: {
						after: this.providerTitlesData.pageInfo?.endCursor,
					},
					updateQuery(previousResult, { fetchMoreResult }) {
						return {
							newTitles: {
								__typename: previousResult.newTitles.__typename,
								totalCount: fetchMoreResult.newTitles.totalCount,
								edges: [...previousResult.newTitles.edges, ...fetchMoreResult.newTitles.edges],
								pageInfo: fetchMoreResult.newTitles.pageInfo,
							},
						};
					},
				})
				.catch(e => {
					captureMessageForSentry(
						'[GraphQL New Page Load More Bucket Titles Issue]:',
						{ error: e, where: 'Component: ProviderTimeline, method: onLoadMore' },
						Sentry.Severity.Error
					);
				})
				.finally(() => complete());
	}

	showSponsoredRecommendation() {
		const isRightProvider = sponsoredRecommendationProviders().includes(this.providerId);
		return this.dayIndex === 0 && isRightProvider;
	}

	showErrorToast() {
		ToastHelper.error({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}
}
